import * as React from 'react';
import { Editor } from '@monaco-editor/react';
import styled from 'styled-components';
import ReactFlow, { Background, Controls, MiniMap, Node as ReactFlowNode, Edge as ReactFlowEdge, Position, ReactFlowInstance, BackgroundVariant } from 'reactflow';
import dagre from 'dagre';
import { Form } from '../create-from-template/type';
import { useFunctionState } from '../../../utils/customHooks';
import { FlexDiv } from '../../products/style';
import {
	PrimaryColor,
	BrightYellowSidely,
	CoralSidely,
	GreySidely,
	PastelPrimaryColor,
	OrangeSidely,
	RedSidely,
	SurfaceColor
} from '../../../styles/global/css/Utils';
import * as _ from 'lodash';
import { PaginationValue } from '../jsonValidator';
import { BoolEditor, EditNextScreens, ErrorWrapper, PageNameEditor, WarningWrapper, Wrapper } from './componentsEditor';
import { addFieldToPage, pageExists, pageValidator } from './formProcessingTools';
import { toast } from 'react-toastify';
import { EditLayout } from './layoutEditor';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import Popup from '../../../components_v2/popup/Popup';
import { LeftModal } from '../FormTemplateCreator';
import { kebabCase } from 'lodash';
import { Field, getFields } from '../../../atoms/forms';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import Close from 'images/icons/orders/close.svg';
import { Translate, translateToNode, translateToString } from '../../../styles/global/translate';
import useAlert from '../../alert/UseAlert';
import { AlertRes } from '../../alert/AlertProvider';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { PanelSelector } from '../../../components_v2/Selector/PanelSelector';



export function launchToastError(message: string) {
	toast.error(message, {
		position: 'top-right',
		autoClose: 3000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		progress: undefined
	});
}

export function launchToastMessage(message: string) {
	toast.info(message, {
		position: 'top-right',
		autoClose: 3000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		progress: undefined
	});
}
const DefaultNodeStyle = {};

const WarningNodeStyle = { border: `2px solid color-mix(in oklab, ${OrangeSidely} 50%, transparent)` };
const WarningSelectedNodeStyle = { background: CoralSidely, border: `2px solid color-mix(in oklab, ${OrangeSidely} 50%, transparent)` };

const ErrorNodeStyle = { border: `2px solid ${RedSidely}` };
const ErrorSelectedNodeStyle = { background: RedSidely, border: `2px solid ${RedSidely}`, color: 'white' };

const SelectedNodeStyle = {
	border: `2px solid ${PrimaryColor}`,
	background: PastelPrimaryColor
};

const InfoSelectedNodeStyle = { border: `1px solid  color-mix(in oklab, ${BrightYellowSidely} 40%, white) `, background: `color-mix(in oklab, ${BrightYellowSidely} 20%, white)` };
const InfoNodeStyle = { background: `color-mix(in oklab, ${BrightYellowSidely} 20%, white)` };

const PanelHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	display: flex;
	align-items: center;
	flex-shrink: 0;
`;

const SettingsSection = styled.div`
	width: 100%;
	font-size: 13px;
	padding: 10px 20px;
`;

const PanelWrapper = styled.div`
	height: calc(100vh - 120px);
	width: calc(69vw - 120px);
	background-color: ${SurfaceColor};
	overflow: auto;
`;

const Label = styled.div`
	font-weight: 500;
`;

export const NoWrapDiv = styled.div`
	text-wrap-mode: nowrap;
	`;

function PanelEditor(props: { pageNumber: number, setPageEditing: (value: number | undefined) => void, fields: Field[] }) {
	const { form, setForm, advancedMode } = React.useContext(FormEditorContext);
	const [addFieldPopup, setAddFieldPopup] = React.useState<boolean>(false);
	const pageName = form[props.pageNumber].name;
	const { fields } = props;
	const alert = useAlert();
	const optionsTab = translateToString('form_editor.options_tab');
	const fieldsTab = translateToString('form_editor.fields_components_tab');
	const codeTab = translateToString('form_editor.code_editor_tab');
	const panels = [{ label: fieldsTab }, { label: optionsTab }, { label: codeTab }];
	const [activePanel, setActivePanel] = React.useState<string>(fieldsTab);
	const [pageCode, setPageCode] = React.useState<string>(JSON.stringify(form[props.pageNumber], null, 2));

	React.useEffect(() => {
		setPageCode(JSON.stringify(form[props.pageNumber], null, 2));
	}, [form, props.pageNumber]);

	return <>
		{pageName && addFieldPopup && <Popup
			popupStyle={{ noTransition: true }}
			isOpen={addFieldPopup} popupMode={PopupMode.Details} onClickOut={() => setAddFieldPopup(false)} content={
				<>
					<div style={{ width: '100%', height: 'calc(100vh - 120px)' }}>
						<LeftModal
							externalUse={true}
							allowEdit={false}
							onFieldClicked={(f) => {
								setForm(addFieldToPage(form, pageName, {
									slug: f.slug,
									metadata: {}
								}));
								setAddFieldPopup(false);
							}}
							errors={[]}
						/>
					</div>
				</>}
		/>}
		<FlexDiv padding='10px 15px 0 15px' gap='6px' justify='space-between' position='sticky' backgroundColor={SurfaceColor} style={{ top: 0, zIndex: 100 }}>
			<div style={{ fontSize: '14px' }}><Translate id='form_editor.editing_page' /> : <span style={{ fontWeight: '500' }}>{form[props.pageNumber].name}</span></div>
			<CloseButton onClick={() => props.setPageEditing(undefined)}>
				<img style={{ width: '24px', aspectRatio: '1 / 1' }} src={Close} alt='close' />
			</CloseButton>
		</FlexDiv>
		{advancedMode &&
			<div style={{ padding: '10px 0', position: 'sticky', top: '42px', backgroundColor: SurfaceColor, zIndex: 100 }}>
				<PanelSelector
					borderBottom
					panels={panels}
					activeIndex={panels.findIndex((panel) => panel.label === activePanel)}
					onClick={(panel) => {
						setActivePanel(panel.label);
					}}
				/>
			</div>
		}
		{activePanel === optionsTab &&
			<SettingsSection>
				<PanelHeader>
					<PageNameEditor path='name' pageNumber={props.pageNumber} />
					<DefaultButton
						buttonStyle={ButtonStyle.Error}
						onClick={() => {
							alert({
								title: translateToString('form_editor.delete_page_title'),
								content: <>{translateToString('form_editor.delete_page_content', [['pageName', pageName ?? '']])}</>,
								buttons: [
									{
										title: translateToNode('cancel'),
										res: AlertRes.Break,
										style: ButtonStyle.White
									},
									{
										title: translateToNode('yes'),
										res: AlertRes.Ok,
										style: ButtonStyle.Error
									}
								],
							}).then((res) => {
								if (res.res === AlertRes.Ok) {
									props.setPageEditing(undefined);
									setForm(form.filter((_, i) => i !== props.pageNumber));
								}
							});
						}}
					>
						<NoWrapDiv><Translate id='form_editor.delete_page' /></NoWrapDiv>
					</DefaultButton>
				</PanelHeader>
				<FlexDiv flow='column' align='stretch' gap='12px'>
					<FlexDiv flow='column' align='stretch' gap='8px'>
						<Label><Translate id='form_editor.progression_label' /></Label>
						<FlexDiv gap='4px' padding='0 0 0 8px'>
							<BoolEditor defaultIsEnabled path='progression' pageNumber={props.pageNumber} />
							<Translate id='form_editor.enable_progression' />
						</FlexDiv>
					</FlexDiv>
					<FlexDiv flow='column' align='stretch' gap='8px'>
						<Label><Translate id='form_editor.next_page_label' /></Label>
						<FlexDiv flow='column' align='stretch' gap='4px' padding='0 0 0 8px'>
							<EditNextScreens path={'next_screens'} pageNumber={props.pageNumber} component={form[props.pageNumber].next_screens} />
						</FlexDiv>
					</FlexDiv>
				</FlexDiv>
			</SettingsSection>
		}
		{
			activePanel === fieldsTab &&
			<div style={{ width: '100%' }}>
				<DragDropContext onDragEnd={(result: DropResult) => {
					if (!result.destination || result.source.droppableId !== result.destination.droppableId) return;
					const newForm = _.cloneDeep(form);
					const children = _.get(newForm[props.pageNumber], result.source.droppableId);
					const [removed] = children.splice(result.source.index, 1);
					children.splice(result.destination.index, 0, removed);
					setForm(newForm);
				}}>
					<EditLayout fieldList={fields} path='layout' pageNumber={props.pageNumber} layout={form[props.pageNumber].layout} />
				</DragDropContext>
			</div>
		}
		{
			activePanel === codeTab &&
			<div style={{ height: '75%', width: '100%' }}>
				<div style={{ paddingLeft: '15px' }}>
					<DefaultButton
						disabled={pageCode === JSON.stringify(form[props.pageNumber], null, 2)}
						onClick={() => {
							try {
								const newPage = JSON.parse(pageCode);
								setForm(form.map((page, index) => index == props.pageNumber ? newPage : page));
							} catch (_e) {
								launchToastError(translateToString('form_editor.syntax_error'));
							}
						}}>
						<Translate id='save' />
					</DefaultButton>
				</div>
				<Editor
					height='100%'
					value={pageCode}
					language='json'
					onChange={(value) => {
						if (value)
							setPageCode(value);
					}}
				/>
			</div>
		}
	</>;
}

export const FormEditorContext = React.createContext<{
	form: Form
	setForm: React.Dispatch<React.SetStateAction<Form>>
	advancedMode: boolean
	appendField:(field: Field) => void
		}>({ form: {} as Form, setForm: () => undefined, advancedMode: false, appendField: () => undefined });

function parseFormSafe(value: string): Form {
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch (_e) {
		return [];
	}
}

function FormEditorProvider(props: { form: string, setForm: (value: string) => void, advancedMode: boolean, appendField: (field: Field) => void, children: React.ReactNode }): JSX.Element {
	const [form, setForm] = useFunctionState<Form>(parseFormSafe(props.form), React.useCallback(({ newValue }) => {
		props.setForm(JSON.stringify(newValue, null, 2));
		return newValue;
	}, []));

	React.useEffect(() => {
		setForm(parseFormSafe(props.form));
	}, [props.form]);

	return <FormEditorContext.Provider value={{ form, setForm, advancedMode: props.advancedMode, appendField: props.appendField }}>
		{props.children}
	</FormEditorContext.Provider>;
}

function calc_layouted_elements(form: Form, fields: Field[], pageEditing: number | undefined): { nodes: ReactFlowNode[], edges: ReactFlowEdge[] } {
	const initialEdges: ReactFlowEdge[] = [];
	const initialNodes: ReactFlowNode[] = [];

	form.forEach((screen, index) => {
		if (screen.name && fields) {
			let style = DefaultNodeStyle;

			if (index == pageEditing) {
				switch (pageValidator(form, screen.name, fields)) {
					case 'warning':
						style = WarningSelectedNodeStyle;
						break;
					case 'error':
						style = ErrorSelectedNodeStyle;
						break;
					case 'info':
						style = InfoSelectedNodeStyle;
						break;
					default:
						style = SelectedNodeStyle;
						break;
				}
			} else {
				switch (pageValidator(form, screen.name, fields)) {
					case 'warning':
						style = WarningNodeStyle;
						break;
					case 'error':
						style = ErrorNodeStyle;
						break;
					case 'info':
						style = InfoNodeStyle;
						break;
					default:
						break;
				}
			}

			initialNodes.push({
				id: screen.name,
				position: { x: 0, y: 0 },
				data: { label: screen.name, index: index },
				style: style
			});
		}
	});

	const UpStyle = { stroke: GreySidely };
	const DownStyle = { stroke: PrimaryColor, strokeWidth: 3 };


	form.forEach((screen) => {
		screen.next_screens.forEach(nextScreen => {
			if (nextScreen && nextScreen.screen_name && screen.name) {
				initialEdges.push({
					id: `${screen.name}-${nextScreen.screen_name}`,
					source: screen.name,
					target: nextScreen.screen_name,
					style: UpStyle,
				});
			}
		});

		screen.components.forEach(component => {
			if (component.type == 'pagination' && typeof component.data == 'object' && !Array.isArray(component.data)) {
				const pagination = component.data;
				if (pagination && pagination['value']) {
					pagination['value'].forEach((value: PaginationValue) => {
						if (value.screen && value.screen.screen_name && screen.name) {
							if (pageExists(form, value.screen.screen_name)) {
								initialEdges.push({
									id: `${screen.name}-${value.screen.screen_name}`,
									target: value.screen.screen_name,
									source: screen.name,
									style: DownStyle,
									// markerEnd: ArrowDown,
								});
							} else {
								if (!value.screen.calculated_screen_name) {
									initialEdges.push({
										id: `${screen.name}-${value.screen.screen_name}-error`,
										target: `${value.screen.screen_name}-error`,
										source: screen.name,
										style: DownStyle,
										// markerEnd: ArrowDown,
									});
									initialNodes.push({
										id: `${value.screen.screen_name}-error`,
										position: { x: 0, y: 0 },
										data: { label: value.screen.screen_name, index: -1 },
										style: { background: RedSidely },
									});
								}
							}
						}
					});
				}
			}
			else if (component.type === 'links' && Array.isArray(component.data)) {
				component.data.forEach((link: string) => {
					if (screen.name && link != '')
						if (pageExists(form, link)) {
							initialEdges.push({
								id: `${screen.name}-${link}`,
								target: link,
								source: screen.name,
								style: DownStyle,
							});
						}
						else {
							initialEdges.push({
								id: `${screen.name}-${link}-error`,
								target: `${link}-error`,
								source: screen.name,
								style: DownStyle,
								// markerEnd: ArrowDown,
							});
							initialNodes.push({
								id: `${link}-error`,
								position: { x: 0, y: 0 },
								data: { label: link, index: -1 },
								style: { background: RedSidely },
							});
						}
				});
			}
			else if (component.screen && screen.name) {
				if (component.screen.screen_name && pageExists(form, component.screen.screen_name)) {
					initialEdges.push({
						id: `${screen.name}-${component.screen.screen_name}`,
						target: component.screen.screen_name,
						source: screen.name,
						style: DownStyle,
					});
				} else {
					if (!component.screen.calculated_screen_name) {
						initialEdges.push({
							id: `${screen.name}-${component.screen.screen_name}-error`,
							target: `${component.screen.screen_name}-error`,
							source: screen.name,
							style: DownStyle,
						});
						initialNodes.push({
							id: `${component.screen.screen_name}-error`,
							position: { x: 0, y: 0 },
							data: { label: component.screen.screen_name, index: -1 },
							style: { background: RedSidely },
						});
					}
				}
			}
		});
	});

	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));


	const nodeWidth = 172;
	const nodeHeight = 36;

	const getLayoutedElements = (nodes: ReactFlowNode[], edges: ReactFlowEdge[], direction = 'TB') => {
		const isHorizontal = direction === 'LR';

		dagreGraph.setGraph({ rankdir: direction });

		nodes.forEach((node) => {
			dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
		});

		edges.forEach((edge) => {
			dagreGraph.setEdge(edge.source, edge.target);
		});

		dagre.layout(dagreGraph);

		nodes.forEach((node) => {
			const nodeWithPosition = dagreGraph.node(node.id);
			node.targetPosition = isHorizontal ? Position.Left : Position.Top;
			node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;
			node.position = {
				x: nodeWithPosition.x - nodeWidth / 2,
				y: nodeWithPosition.y - nodeHeight / 2,
			};
			return node;
		});

		return { nodes, edges };
	};

	return getLayoutedElements(
		initialNodes,
		initialEdges
	);
}


function _VisualFormEditor(props: { fields?: Field[] }) {
	const { form } = React.useContext(FormEditorContext);
	const [pageEditing, setPageEditing] = React.useState<number | undefined>(undefined);
	const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | undefined>(undefined);
	const [layoutedEdges, setLayoutedEdges] = React.useState<ReactFlowEdge[]>([]);
	const [layoutedNodes, setLayoutedNodes] = React.useState<ReactFlowNode[]>([]);
	const [panelReady, setPanelReady] = React.useState(false);
	const fields = props.fields;
	const prevFormLengthRef = React.useRef(form.length);

	React.useEffect(() => {
		if (form.length > prevFormLengthRef.current) {
			setPageEditing(form.length - 1);
		} else if (pageEditing !== undefined && pageEditing >= form.length) {
			setPageEditing(undefined);
		}
		prevFormLengthRef.current = form.length;
	}, [form.length, pageEditing]);

	React.useEffect(() => {
		if (form && fields) {
			const { nodes, edges } = calc_layouted_elements(form, fields, pageEditing);
			setLayoutedEdges(edges);
			setLayoutedNodes(nodes);
		}
	}, [JSON.stringify(form), fields, pageEditing]);


	React.useEffect(() => {
		setTimeout(() => reactFlowInstance?.fitView(), 0);
		setPanelReady(pageEditing !== undefined);
	}, [pageEditing]);



	function nodeClickEvent(event: React.MouseEvent, node: ReactFlowNode) {
		if (!node.id.includes('-error')) {
			const wasUndefined = pageEditing === undefined;
			setPageEditing(node.data.index);
			if (wasUndefined) {
				setTimeout(() => reactFlowInstance?.fitView(), 0);
			}
		} else {
			setPageEditing(undefined);
			launchToastError(node.id.replace('-error', '') + ' does not exist in this form.');
		}
	}

	function onInit(instance: ReactFlowInstance) {
		setReactFlowInstance(instance);
	}
	return <>
		<FlexDiv
			style={
				{
					overflow: 'auto',
				}
			}
		>

			{pageEditing != undefined && panelReady && <PanelWrapper>
				<PanelEditor setPageEditing={setPageEditing} pageNumber={pageEditing} fields={fields ?? []} />
			</PanelWrapper>}
			<div style={{ width: (pageEditing != undefined ? 33 : 100) + 'vw', height: 'calc(100vh - 120px)' }}>
				{layoutedNodes.length >= 1 && <ReactFlow
					fitView={true}
					nodes={layoutedNodes}
					edges={layoutedEdges}
					onNodeClick={nodeClickEvent}
					onInit={onInit}
					proOptions={{ hideAttribution: true }}
				>
					<Controls />
					<MiniMap />
					<Background variant={BackgroundVariant.Dots} gap={12} size={1} />
				</ReactFlow>}
			</div>
		</FlexDiv>
	</>;
}

function check_if_form_uses_id(form: Form): boolean {
	if (!Array.isArray(form)) return false;
	return form.some((page) => Array.isArray(page?.fields) && page.fields.some((field) => field?.field_id !== undefined));
}

function update_form_to_use_slugs(form: Form, fields: Field[], lib: string, setLib: React.Dispatch<React.SetStateAction<string>>): Form {
	const newForm = _.cloneDeep(form);
	const replace_acc: Record<string, string>[] = [];
	const replace_id_acc: Record<string, string>[] = [];



	newForm.map((page) => {
		page.fields.map((field) => {
			if (field && field.field_id) {
				const newField = fields.find((f) => f.id === field.field_id);
				if (newField) {
					lib = lib.replaceAll(field.field_id.toString(), '"' + newField.slug + '"');
					replace_id_acc.push({ old: field.field_id.toString(), new: newField.slug });
				}
				if (newField && field.name) {
					replace_acc.push({ old: field.name, new: newField.slug });
					field.slug = newField.slug;
					field.name = undefined;
					field.field_id = undefined;
				}
				else {
					field.slug = field.name ? kebabCase(field.name) : 'undefined';
					field.field_id = undefined;
					field.name = undefined;
				}
			}
		});
	});
	let anotherNewForm = JSON.parse(JSON.stringify(newForm));

	replace_acc.forEach((replace) => {
		if (replace.old !== 'type' && replace.old !== 'slug' && replace.old !== 'metadata' && replace.old !== 'field_id' && replace.old !== 'name' && replace.old !== 'value') {
			anotherNewForm = JSON.parse(JSON.stringify(anotherNewForm).replaceAll('"' + replace.old + '"', '"' + replace.new + '"'));
			anotherNewForm = JSON.parse(JSON.stringify(anotherNewForm).replaceAll('\\"' + replace.old + '\\"', '\\"' + replace.new + '\\"'));
		}
	});

	replace_id_acc.forEach((replace) => {
		anotherNewForm = JSON.parse(JSON.stringify(anotherNewForm, null, 2).replaceAll('getSavedFieldByIdAndCurrentMetadataAndSubMetadata(' + replace.old, 'getSavedFieldByIdAndCurrentMetadataAndSubMetadata(' + '\\"' + replace.new + '\\"'));
		anotherNewForm = JSON.parse(JSON.stringify(anotherNewForm, null, 2).replaceAll('getSavedFieldByIdAndCurrentMetadata(' + replace.old, 'getSavedFieldByIdAndCurrentMetadata(' + '\\"' + replace.new + '\\"'));
	});

	setLib(lib);
	return anotherNewForm;
}


// eslint-disable-next-line react/display-name
const VisualFormEditor = React.forwardRef((props: {
	screens: string,
	setScreens: (value: string) => void,
	lib: string,
	setLib: React.Dispatch<React.SetStateAction<string>>,
	advancedMode: boolean,
	exportForm: () => void
}, parentRef) => {
	React.useImperativeHandle(parentRef, () => ({
		onErrorClicked: () => undefined,
		onFieldClicked: () => undefined
	}), []);
	const [fields, setFields] = React.useState<Field[]>();
	const hasWeird = props.lib.includes('evaluateChildFunction');
	const hasIndex = props.screens.includes('"index":');
	const alert = useAlert();

	React.useEffect(() => {
		getFields().then(setFields);
	}, []);

	let parsedScreens: Form | null = null;
	try { parsedScreens = JSON.parse(props.screens); } catch (_e) { /* invalid JSON */ }

	if (parsedScreens && Array.isArray(parsedScreens) && check_if_form_uses_id(parsedScreens) && fields) {

		return <>
			<div>
				{!hasIndex && <Wrapper>
					<Translate id='form_editor.update_required' />
				</Wrapper>}
				{hasWeird && !hasIndex && <WarningWrapper>
					<Translate id='form_editor.custom_functions_warning' />
				</WarningWrapper>}
				{hasIndex && <ErrorWrapper>
					<Translate id='form_editor.cannot_update' />
				</ErrorWrapper>}
			</div>
			{!hasIndex && <DefaultButton
				onClick={
					() => {
						//
						alert({
							title: translateToString('form_editor.convert.you_are_converting'),
							content: <Translate id='form_editor.convert.have_you_made_backup' />,
							buttons: [
								{
									title: translateToNode('cancel'),
									res: AlertRes.Break,
									style: ButtonStyle.White
								},
								{
									title: translateToNode('yes'),
									res: AlertRes.Ok,
									style: ButtonStyle.Error
								}
							],
						}).then((res) => {
							if (res.res === AlertRes.Ok) {
								props.exportForm();
								props.setScreens(JSON.stringify(update_form_to_use_slugs(parsedScreens!, fields, props.lib, props.setLib), null, 2));
							}
						});
					}
				}>
				<Translate id='form_editor.update_form_button' />
			</DefaultButton>}
		</>;
	}

	return <>
		<FormEditorProvider
			form={props.screens}
			setForm={props.setScreens}
			advancedMode={props.advancedMode}
			appendField={(field) => setFields(prev => [...(prev ?? []), field])}
		>
			<_VisualFormEditor fields={fields} />
		</FormEditorProvider>
	</>;

});

export default VisualFormEditor;
